import { useMutation, useQueryClient, useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useCurrentUser } from "./useCurrentUser";
import { toast } from "./use-toast";
import { useConnectionStatus } from "./useUserConnections";

export function useUserConnection(targetUserId: string) {
  const { data: user } = useCurrentUser();
  const queryClient = useQueryClient();

  // Use the new connection status hook
  const { data: connectionData, isLoading } = useConnectionStatus(targetUserId);

  const connectMutation = useMutation({
    mutationFn: async (message?: string) => {
      if (!user) throw new Error("Must be logged in to connect");
      
      const { data, error } = await supabase.functions.invoke('connection-request', {
        body: {
          action: 'create',
          recipientId: targetUserId,
          message: message || undefined,
        }
      });
      
      if (error) throw error;
      
      // Check for rate limit error
      if (data?.rateLimitExceeded) {
        const rateLimitError = new Error(data.error || "Rate limit exceeded");
        (rateLimitError as any).rateLimitData = data;
        throw rateLimitError;
      }
      
      return data;
    },
    onSuccess: (data: any) => {
      queryClient.invalidateQueries({ queryKey: ["user-connection"] });
      queryClient.invalidateQueries({ queryKey: ["connection-status"] });
      queryClient.invalidateQueries({ queryKey: ["connection-rate-limit"] });
      
      if (data?.alreadyExists) {
        // Connection already exists - show appropriate message based on status
        const status = data.connection?.status;
        if (status === 'pending') {
          toast({
            title: "Request Already Sent",
            description: "You already have a pending connection request with this user",
          });
        } else if (status === 'accepted') {
          toast({
            title: "Already Connected",
            description: "You're already connected with this user",
          });
        }
      } else {
        toast({
          title: "Connection Request Sent",
          description: "Your connection request has been sent",
        });
      }
    },
    onError: (error: any) => {
      // Handle rate limit errors specially
      if (error.rateLimitData) {
        toast({
          title: "Rate Limit Reached",
          description: error.message,
          variant: "destructive",
        });
      } else {
        toast({
          title: "Error",
          description: error.message || "Failed to send connection request",
          variant: "destructive",
        });
      }
    },
  });

  const disconnectMutation = useMutation({
    mutationFn: async () => {
      if (!user || !connectionData?.connectionId) throw new Error("Must be logged in");
      
      const { data, error } = await supabase.functions.invoke('connection-request', {
        body: {
          action: 'remove',
          connectionId: connectionData.connectionId
        }
      });
      
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["user-connection"] });
      queryClient.invalidateQueries({ queryKey: ["connection-status"] });
      toast({
        title: "Disconnected",
        description: "Connection removed successfully",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || "Failed to disconnect",
        variant: "destructive",
      });
    },
  });

  const withdrawMutation = useMutation({
    mutationFn: async () => {
      if (!user || !connectionData?.connectionId) throw new Error("Must be logged in");
      
      const { data, error } = await supabase.functions.invoke('connection-request', {
        body: {
          action: 'remove',
          connectionId: connectionData.connectionId
        }
      });
      
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["user-connection"] });
      queryClient.invalidateQueries({ queryKey: ["connection-status"] });
      toast({
        title: "Request Withdrawn",
        description: "Connection request has been withdrawn",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || "Failed to withdraw request",
        variant: "destructive",
      });
    },
  });

  const acceptMutation = useMutation({
    mutationFn: async () => {
      if (!user || !connectionData?.connectionId) throw new Error("Must be logged in");
      
      const { data, error } = await supabase.functions.invoke('connection-request', {
        body: {
          action: 'accept',
          connectionId: connectionData.connectionId
        }
      });
      
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["user-connection"] });
      queryClient.invalidateQueries({ queryKey: ["connection-status"] });
      queryClient.invalidateQueries({ queryKey: ["pending-connection-requests"] });
      toast({
        title: "Connection Accepted",
        description: "You are now connected!",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || "Failed to accept connection",
        variant: "destructive",
      });
    },
  });

  const declineMutation = useMutation({
    mutationFn: async () => {
      if (!user || !connectionData?.connectionId) throw new Error("Must be logged in");
      
      const { data, error } = await supabase.functions.invoke('connection-request', {
        body: {
          action: 'decline',
          connectionId: connectionData.connectionId
        }
      });
      
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["user-connection"] });
      queryClient.invalidateQueries({ queryKey: ["connection-status"] });
      queryClient.invalidateQueries({ queryKey: ["pending-connection-requests"] });
      toast({
        title: "Request Declined",
        description: "Connection request declined",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || "Failed to decline connection",
        variant: "destructive",
      });
    },
  });

  // Check if this is an incoming request
  const isIncomingRequest = connectionData?.status === 'pending' && 
    connectionData?.connectionId && 
    user?.id !== undefined;

  // Query to determine if user is recipient
  const { data: connectionDetails } = useQuery({
    queryKey: ["connection-details", connectionData?.connectionId],
    queryFn: async () => {
      if (!connectionData?.connectionId) return null;
      
      const { data } = await supabase
        .from("user_connections")
        .select("recipient_id, requester_id")
        .eq("id", connectionData.connectionId)
        .single();
      
      return data;
    },
    enabled: !!connectionData?.connectionId && connectionData?.status === 'pending',
  });

  const isIncoming = isIncomingRequest && connectionDetails?.recipient_id === user?.id;
  const isOutgoing = isIncomingRequest && connectionDetails?.requester_id === user?.id;

  const toggleConnect = () => {
    if (!user) {
      toast({
        title: "Sign in required",
        description: "Please sign in to connect with team members",
        variant: "destructive",
      });
      return;
    }
    
    if (connectionData?.isConnected) {
      disconnectMutation.mutate();
    } else {
      connectMutation.mutate("");
    }
  };

  return {
    isConnected: connectionData?.isConnected || false,
    connectionStatus: connectionData?.status,
    connectionId: connectionData?.connectionId,
    isIncomingRequest: isIncoming || false,
    isOutgoingRequest: isOutgoing || false,
    isLoading,
    connect: connectMutation.mutate,
    toggleConnect,
    disconnect: disconnectMutation.mutate,
    withdrawRequest: withdrawMutation.mutate,
    acceptRequest: acceptMutation.mutate,
    declineRequest: declineMutation.mutate,
    isUpdating: connectMutation.isPending || disconnectMutation.isPending || 
                withdrawMutation.isPending || acceptMutation.isPending || 
                declineMutation.isPending,
  };
}
